/**
 * Fee Calculation Utilities
 * Rule-based service fee and delivery fee calculations
 */

import type { OrderType, Restaurant } from '@/types';

interface ServiceFeeRule {
  id: string;
  name: string;
  min_amount: number;
  max_amount: number | null;
  fee_type: 'flat' | 'percentage';
  fee_value: number;
  min_fee?: number;
  max_fee?: number;
  enabled: boolean;
}

interface DeliveryFeeRule {
  id: string;
  name: string;
  max_distance_miles: number | null;
  fee_type: 'flat' | 'percentage';
  fee_value: number;
  min_delivery_fee?: number | null;
  max_delivery_fee?: number | null;
  min_order: number;
  free_delivery_threshold: number;
  enabled: boolean;
}

/**
 * Calculate service fee based on restaurant's fee rules
 * @param subtotal - Order subtotal amount
 * @param restaurant - Restaurant with service_fee_rules
 * @returns Calculated service fee
 */
export function calculateServiceFee(
  subtotal: number,
  restaurant: Restaurant | null | undefined
): number {
  if (!restaurant) return 0;

  // Use rule-based system if configured
  if (
    restaurant.service_fee_rules &&
    Array.isArray(restaurant.service_fee_rules) &&
    restaurant.service_fee_rules.length > 0
  ) {
    for (const rule of restaurant.service_fee_rules as ServiceFeeRule[]) {
      // Skip disabled rules
      if (!rule.enabled) continue;

      // Check if subtotal falls within rule's range
      const minAmount = rule.min_amount || 0;
      const maxAmount = rule.max_amount;

      if (subtotal < minAmount) continue;
      if (maxAmount !== null && subtotal > maxAmount) continue;

      // Calculate fee based on rule type
      if (rule.fee_type === 'flat') {
        return rule.fee_value;
      } else if (rule.fee_type === 'percentage') {
        let fee = subtotal * (rule.fee_value / 100);

        // Apply min/max constraints if specified
        if (rule.min_fee !== undefined && rule.min_fee !== null) {
          fee = Math.max(fee, rule.min_fee);
        }
        if (rule.max_fee !== undefined && rule.max_fee !== null) {
          fee = Math.min(fee, rule.max_fee);
        }

        return parseFloat(fee.toFixed(2));
      }
    }
  }

  // Fallback to legacy calculation
  const serviceFeePercent = Number(restaurant.service_fee_percent) || 5;
  const serviceFeeMin = Number(restaurant.service_fee_min) || 0.5;
  const serviceFeeMax = Number(restaurant.service_fee_max) || 3.0;

  const fee = Math.min(
    Math.max(subtotal * (serviceFeePercent / 100), serviceFeeMin),
    serviceFeeMax
  );

  return parseFloat(fee.toFixed(2));
}

/**
 * Calculate delivery fee based on restaurant's delivery fee rules
 * @param subtotal - Order subtotal amount
 * @param restaurant - Restaurant with delivery_fee_rules
 * @param orderType - 'delivery' or 'collection'
 * @param distanceMiles - Optional distance from restaurant (for distance-based rules)
 * @returns Calculated delivery fee
 */
export function calculateDeliveryFee(
  subtotal: number,
  restaurant: Restaurant | null | undefined,
  orderType: OrderType,
  distanceMiles?: number | null
): number {
  if (!restaurant || orderType === 'collection') return 0;

  // Use rule-based system if configured
  if (
    restaurant.delivery_fee_rules &&
    Array.isArray(restaurant.delivery_fee_rules) &&
    restaurant.delivery_fee_rules.length > 0
  ) {
    // When no distance provided, use the first enabled rule (default zone)
    // Don't check min_order for estimation - just return the fee
    const firstRule = restaurant.delivery_fee_rules.find(
      (r: any) => (r as DeliveryFeeRule).enabled
    );

    if (firstRule && distanceMiles === undefined) {
      const rule = firstRule as DeliveryFeeRule;

      // Check free delivery threshold
      const freeThreshold = rule.free_delivery_threshold || 0;
      if (freeThreshold > 0 && subtotal >= freeThreshold) {
        return 0;
      }

      // Calculate fee based on rule type
      let fee = 0;
      if (rule.fee_type === 'flat') {
        fee = rule.fee_value;
      } else if (rule.fee_type === 'percentage') {
        fee = subtotal * (rule.fee_value / 100);

        // Apply min/max constraints for percentage fees only
        if (rule.min_delivery_fee !== undefined && rule.min_delivery_fee !== null) {
          fee = Math.max(fee, rule.min_delivery_fee);
        }
        if (rule.max_delivery_fee !== undefined && rule.max_delivery_fee !== null) {
          fee = Math.min(fee, rule.max_delivery_fee);
        }
      }

      return parseFloat(fee.toFixed(2));
    }

    // When distance IS provided, check all rules properly
    for (const rule of restaurant.delivery_fee_rules as DeliveryFeeRule[]) {
      if (!rule.enabled) continue;

      // Check distance limit
      const maxDistance = rule.max_distance_miles;
      if (maxDistance !== null && distanceMiles !== undefined && distanceMiles !== null) {
        if (distanceMiles > maxDistance) {
          continue;
        }
      }

      // Check minimum order requirement
      const minOrder = rule.min_order || 0;
      if (subtotal < minOrder) {
        continue;
      }

      // Check free delivery threshold
      const freeThreshold = rule.free_delivery_threshold || 0;
      if (freeThreshold > 0 && subtotal >= freeThreshold) {
        return 0;
      }

      // Calculate fee based on rule type
      let fee = 0;
      if (rule.fee_type === 'flat') {
        fee = rule.fee_value;
      } else if (rule.fee_type === 'percentage') {
        fee = subtotal * (rule.fee_value / 100);

        // Apply min/max constraints for percentage fees only
        if (rule.min_delivery_fee !== undefined && rule.min_delivery_fee !== null) {
          fee = Math.max(fee, rule.min_delivery_fee);
        }
        if (rule.max_delivery_fee !== undefined && rule.max_delivery_fee !== null) {
          fee = Math.min(fee, rule.max_delivery_fee);
        }
      }

      return parseFloat(fee.toFixed(2));
    }
  }

  // Fallback to legacy calculation only if no rules exist
  const freeDeliveryThreshold = Number(restaurant.free_delivery_threshold) || 50;
  if (subtotal >= freeDeliveryThreshold) {
    return 0;
  }

  const deliveryFee = Number(restaurant.delivery_fee) || 2.99;
  return deliveryFee;
}

/**
 * Calculate minimum order for delivery based on restaurant's delivery fee rules
 * @param restaurant - Restaurant with delivery_fee_rules
 * @param distanceMiles - Optional distance from restaurant
 * @returns Minimum order amount for delivery
 */
export function getMinimumOrder(
  restaurant: Restaurant | null | undefined,
  distanceMiles?: number | null
): number {
  if (!restaurant) return 0;

  // Use rule-based system if configured
  if (
    restaurant.delivery_fee_rules &&
    Array.isArray(restaurant.delivery_fee_rules) &&
    restaurant.delivery_fee_rules.length > 0
  ) {
    for (const rule of restaurant.delivery_fee_rules as DeliveryFeeRule[]) {
      // Skip disabled rules
      if (!rule.enabled) continue;

      // Check distance limit (if specified and distance provided)
      const maxDistance = rule.max_distance_miles;
      if (maxDistance !== null && distanceMiles !== undefined && distanceMiles !== null) {
        if (distanceMiles > maxDistance) {
          continue; // Skip this rule, try next
        }
      }

      // Return the minimum order for this rule
      return rule.min_order || 0;
    }
  }

  // Fallback to legacy minimum_order
  return Number(restaurant.minimum_order) || 10;
}

/**
 * Get free delivery threshold based on restaurant's delivery fee rules
 * @param restaurant - Restaurant with delivery_fee_rules
 * @param distanceMiles - Optional distance from restaurant
 * @returns Free delivery threshold amount
 */
export function getFreeDeliveryThreshold(
  restaurant: Restaurant | null | undefined,
  distanceMiles?: number | null
): number {
  if (!restaurant) return 0;

  // Use rule-based system if configured
  if (
    restaurant.delivery_fee_rules &&
    Array.isArray(restaurant.delivery_fee_rules) &&
    restaurant.delivery_fee_rules.length > 0
  ) {
    for (const rule of restaurant.delivery_fee_rules as DeliveryFeeRule[]) {
      // Skip disabled rules
      if (!rule.enabled) continue;

      // Check distance limit (if specified and distance provided)
      const maxDistance = rule.max_distance_miles;
      if (maxDistance !== null && distanceMiles !== undefined && distanceMiles !== null) {
        if (distanceMiles > maxDistance) {
          continue; // Skip this rule, try next
        }
      }

      // Return the free delivery threshold for this rule
      return rule.free_delivery_threshold || 0;
    }
  }

  // Fallback to legacy free_delivery_threshold
  return Number(restaurant.free_delivery_threshold) || 25;
}

/**
 * Get the minimum (lowest) delivery fee from restaurant's delivery fee rules
 * Used to show estimated delivery fee before postcode is entered
 * @param restaurant - Restaurant with delivery_fee_rules
 * @returns Minimum delivery fee
 */
export function getMinimumDeliveryFee(restaurant: Restaurant | null | undefined): number {
  if (!restaurant) return 2.99;

  // Use rule-based system if configured
  if (
    restaurant.delivery_fee_rules &&
    Array.isArray(restaurant.delivery_fee_rules) &&
    restaurant.delivery_fee_rules.length > 0
  ) {
    let minFee = Infinity;

    for (const rule of restaurant.delivery_fee_rules as DeliveryFeeRule[]) {
      // Skip disabled rules
      if (!rule.enabled) continue;

      // Calculate fee for this rule (assume subtotal meets threshold for now)
      if (rule.fee_type === 'flat') {
        // Only consider non-zero fees for minimum estimation
        if (rule.fee_value > 0) {
          minFee = Math.min(minFee, rule.fee_value);
        }
      }
      // For percentage fees, we can't determine the minimum without knowing subtotal
      // so we skip percentage-based rules
    }

    // If no flat fee found, fall back to legacy delivery_fee
    if (minFee === Infinity) {
      const legacyFee = Number(restaurant.delivery_fee) || 0;
      // Never return 0 as minimum - use 2.99 as absolute fallback
      return legacyFee > 0 ? legacyFee : 2.99;
    }

    return minFee;
  }

  // Fallback to legacy delivery_fee
  const legacyFee = Number(restaurant.delivery_fee) || 0;
  return legacyFee > 0 ? legacyFee : 2.99;
}
